import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Sparkles, RotateCcw, Layers, Ruler } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { WIDGET_CATALOG, isWidgetVisible } from '../data/displayPrefs';

/**
 * Display Preferences — central settings page for hiding/showing widgets
 * across the app. Reachable from any widget's `⋯` menu, from the Profile
 * page, and from each gated page's header gear icon.
 */
const DisplayPrefs = () => {
  const { profile, update, setDisplayPref, setDisplayPreset, resetDisplayPrefs } = useProfile();
  const prefs = profile.displayPrefs || {};
  const preset = prefs.preset || 'default';
  const units = profile.units || 'imperial';

  const PRESETS = [
    {
      id: 'default',
      label: 'Full',
      blurb: 'Everything on. All modules, all widgets, all cards.',
    },
    {
      id: 'simplified',
      label: 'Simplified',
      blurb: 'Dashboard trimmed to essentials. Strips coach tips, motivators, and fuel reminders.',
    },
    {
      id: 'skeleton',
      label: 'Skeleton',
      blurb: 'Only Progress, Strength, Library, Impact in the nav. Everything else off by default. Turn things back on as you need them.',
    },
  ];

  const reasonForHidden = (page, w) => {
    if (w.adaptiveOnly && !profile.adaptiveMode) return 'Adaptive Mode only';
    if (w.requiresNutrition && !profile.nutritionGoal) return 'Requires nutrition setup';
    if (w.recommendedFor) {
      const subs = profile.adaptiveSubmodes || [];
      if (!w.recommendedFor.some((s) => subs.includes(s))) return 'Not relevant to your profile';
    }
    return null;
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/profile" className="inline-flex items-center gap-2 text-sm text-enf-text-secondary hover:text-enf-text-primary mb-6" data-testid="display-prefs-back">
          <ArrowLeft className="w-4 h-4" /> Back to profile
        </Link>

        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Display preferences</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mb-3">Just what you need.</h1>
        <p className="text-enf-text-secondary mb-8 max-w-xl text-sm leading-relaxed">
          Hide anything that doesn't help you. Toggle it back on whenever you want.
          Defaults are tuned to what you told us during onboarding — nothing's locked.
        </p>

        {/* Preset selector — 3-way picker per user direction */}
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 mb-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
              <Layers className="w-5 h-5 text-enf-accent-fg" />
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl tracking-wide leading-none mb-1">Display mode</div>
              <p className="text-sm text-enf-text-secondary">
                Choose how much of the app you want to see. You can flip back and forth any time.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {PRESETS.map((p) => {
              const active = preset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setDisplayPreset(p.id)}
                  className={`text-left p-4 rounded-md border transition-colors ${
                    active
                      ? 'bg-enf-surface-elevated border-enf-accent-fg text-enf-text-primary'
                      : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                  }`}
                  data-testid={`display-preset-${p.id}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    {active && <Sparkles className="w-4 h-4 text-enf-accent-fg" />}
                    <span className="font-display text-xl tracking-wide leading-none">{p.label}</span>
                  </div>
                  <div className="text-xs text-enf-text-secondary leading-relaxed">{p.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Units toggle — drives GPS distance / splits / pace display. */}
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 mb-8" data-testid="units-toggle-section">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-md bg-enf-role-info/10 border border-enf-role-info/30 flex items-center justify-center flex-shrink-0">
              <Ruler className="w-5 h-5 text-enf-role-info" />
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl tracking-wide leading-none mb-1">Units</div>
              <p className="text-sm text-enf-text-secondary">
                Applies to distance, pace, and splits on Endurance and Activity history.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'imperial', label: 'Imperial', blurb: 'Miles · ft · °F' },
              { id: 'metric', label: 'Metric', blurb: 'Kilometres · m · °C' },
            ].map((u) => {
              const active = units === u.id;
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => update({ units: u.id })}
                  className={`text-left p-4 rounded-md border transition-colors ${
                    active
                      ? 'bg-enf-surface-elevated border-enf-accent-fg text-enf-text-primary'
                      : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                  }`}
                  data-testid={`units-option-${u.id}`}
                >
                  <div className="font-display text-xl tracking-wide leading-none mb-1">{u.label}</div>
                  <div className="text-xs text-enf-text-secondary">{u.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-widget toggles, grouped by page */}
        <div className="space-y-6 mb-8">
          {Object.entries(WIDGET_CATALOG).map(([pageKey, group]) => (
            <section key={pageKey} className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden" data-testid={`display-prefs-section-${pageKey}`}>
              <div className="px-5 py-3 bg-enf-surface-elevated border-b border-enf-surface-border flex items-center justify-between">
                <div className="font-display text-lg tracking-wider uppercase text-enf-text-secondary">{group.label}</div>
                <div className="text-xs text-enf-text-muted">{group.widgets.length} cards</div>
              </div>
              <div className="divide-y divide-zinc-900">
                {group.widgets.map((w) => {
                  const reason = reasonForHidden(pageKey, w);
                  const visible = isWidgetVisible(pageKey, w.id, profile);
                  const explicit = prefs[pageKey]?.[w.id];
                  return (
                    <div key={w.id} className="px-5 py-4 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-enf-text-primary">{w.label}</div>
                          {explicit === true && <span className="text-[10px] uppercase tracking-widest text-enf-role-info">manual on</span>}
                          {explicit === false && <span className="text-[10px] uppercase tracking-widest text-enf-role-warning">manual off</span>}
                        </div>
                        {w.desc && <div className="text-xs text-enf-text-muted mt-0.5">{w.desc}</div>}
                        {reason && <div className="text-xs text-enf-text-muted mt-1 italic">{reason}</div>}
                      </div>
                      <button
                        type="button"
                        disabled={!!reason}
                        onClick={() => setDisplayPref(pageKey, w.id, !visible)}
                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          reason
                            ? 'bg-enf-surface-mid border border-enf-surface-border text-enf-text-muted cursor-not-allowed'
                            : visible
                              ? 'bg-enf-role-info/10 hover:bg-enf-role-info/20 border border-enf-role-info/30 text-enf-role-info'
                              : 'bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary'
                        }`}
                        data-testid={`display-prefs-toggle-${pageKey}-${w.id}`}
                      >
                        {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {reason ? 'Unavailable' : visible ? 'Visible' : 'Hidden'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <button
          onClick={resetDisplayPrefs}
          className="inline-flex items-center gap-2 text-sm text-enf-text-muted hover:text-enf-text-primary transition-colors"
          data-testid="display-prefs-reset"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset to onboarding defaults
        </button>
      </div>
    </div>
  );
};

export default DisplayPrefs;
